import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  AssessmentOutlined,
  BadgeOutlined,
  BlockOutlined,
  BuildOutlined,
  CellTowerOutlined,
  ContentPasteSearchOutlined,
  InsertChartOutlined,
  RouteOutlined,
  ScaleOutlined,
  SensorsOutlined,
  TireRepairOutlined,
  VideocamOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { Box, Button, Grid } from '@mui/material/';
import { ThemeProvider, styled, useTheme } from '@mui/material/styles';
import { AUTHORITIES, ColorScheme } from 'app/config/constants';
import { IRootState, useAppDispatch, useAppSelector } from 'app/config/store';
import { getEntityByTruckImplementId } from 'app/entities/block-screen/block-screen.reducer';
import { getDeviceByTruckImplementId } from 'app/entities/device-truck-implement/device-truck-implement.reducer';
import { getMaintenanceControlByTruckImplementId } from 'app/entities/maintenance-control/maintenance-control-custom.reducer';
import { hasAnyAuthority, isUserAuthorizedForDeviceBlockByCompany, isTireAuthorizedByCompany } from 'app/shared/auth/private-route';
import { DeviceType } from 'app/shared/model/enumerations/device-type.model';
import { showElement } from 'app/shared/util/data-utils';
import axios from 'axios';
import { Loading } from 'app/shared/util/loading';
import React, { useEffect, useState } from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import TruckImplementInfo from './TruckImplementInfo';

export const TruckImplementInfoCard = ({ isModal, dataProps, ...props }: RouteComponentProps<{ id: string }> | any) => {
  const dispatch = useAppDispatch();

  const isManufacturerAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.MANUFACTURER_ADMIN]));

  const theme = useTheme();

  const isDeviceBlockByCompany = useAppSelector(state =>
    isUserAuthorizedForDeviceBlockByCompany(state.authentication.account.authorities, state.authentication.account.company)
  );
  const isRepresentativeAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.REPRESENTATIVE_ADMIN]));
  const isTransporterAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.TRANSPORTER_ADMIN]));
  const isChecklist = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.CHECKLIST]));
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));
  const loadingDevices = useAppSelector((state: IRootState) => state.deviceTruckImplement.loading);
  const maintenanceControlCustom = useAppSelector(state => state.maintenanceControlCustom.entity);
  const devices = useAppSelector((state: IRootState) => state.deviceTruckImplement.entities);
  const blockScreen = useAppSelector(state => state.blockScreen.entity);
  const truckImplement = useAppSelector(state => state.truckImplement.entity);
  const [hasCamera, setHasCamera] = useState(false);

  const borderVariant = theme.palette.mode === ColorScheme.DARK ? '' : '2px solid #D5D5D5';
  const routeProps = props?.props ?? props;
  const truckId = routeProps?.match?.params?.id ?? routeProps?.id;
  const buttonRoute = routeProps?.match?.url ?? `dashboards/truck-implement/${truckId}`;
  const camerasRoute = `/live-monitor?truckImplementId=${truckId}${
    truckImplement?.licensePlate ? `&licensePlate=${encodeURIComponent(truckImplement.licensePlate)}` : ''
  }${truckImplement?.company?.id ? `&companyId=${truckImplement.company.id}` : ''}${truckImplement?.company?.name ? `&companyName=${encodeURIComponent(truckImplement.company.name)}` : ''}`;
  const alertsRoute = `${buttonRoute}/alerts?tab=cercas&periodic=TODAY&truckImplementId=${truckId}${
    truckImplement?.licensePlate ? `&licensePlateSearch=${encodeURIComponent(truckImplement.licensePlate)}` : ''
  }${truckImplement?.company?.id ? `&companyId=${truckImplement.company.id}` : ''}${truckImplement?.company?.name ? `&companyName=${encodeURIComponent(truckImplement.company.name)}` : ''}`;

  useEffect(() => {
    dispatch(getDeviceByTruckImplementId(truckId));
    dispatch(getMaintenanceControlByTruckImplementId(truckId));
    dispatch(getEntityByTruckImplementId(truckId));
  }, [truckId]);

  useEffect(() => {
    setHasCamera(false);
    if (!isAdmin && !isTransporterAdmin) {
      return;
    }

    let active = true;
    void axios
      .get<boolean>(`api/admin/camera-devices/exists/${truckId}`)
      .then(response => {
        if (active) setHasCamera(response.data === true);
      })
      .catch(() => {
        if (active) setHasCamera(false);
      });

    return () => {
      active = false;
    };
  }, [truckId, isAdmin, isTransporterAdmin]);

  // const handleCheckDeviceExists = async () => {
  //   try {
  //     const exists = await dispatch(checkIfDeviceExistsByTruckImplementId(truckId)).unwrap();
  //     setDeviceExists(exists);
  //   } catch (error) {
  //     console.error('Error checking device existence:', error);
  //   }
  // };

  const ButtonIcon = propsIcon => (
    <Button
      {...propsIcon}
      component={Link}
      sx={{ display: 'flex', flexDirection: 'column', width: '120px', '&:hover': { transform: 'scale(1.2)', transition: 'ease-in-out' } }}
    >
      {propsIcon.children}
    </Button>
  );

  const showButtonByDevice = (deviceType: DeviceType[]) => {
    return devices.some(device => deviceType.includes(device.device.deviceType));
  };

  const companyId = useAppSelector(state => state.authentication.account.company?.id);

  return (
    <ThemeProvider theme={theme}>
      <BoxContainerTruckImplementInfo
        border={borderVariant}
        margin={'0 auto'}
        sx={{
          ...(isModal
            ? {
                boxShadow: 'none',
              }
            : {
                boxShadow:
                  'rgba(0, 0, 0, 0.25) 0px 54px 55px, rgba(0, 0, 0, 0.12) 0px -12px 30px, rgba(0, 0, 0, 0.12) 0px 4px 6px, rgba(0, 0, 0, 0.17) 0px 12px 13px, rgba(0, 0, 0, 0.09) 0px -3px 5px',
              }),
        }}
      >
        <Box component={'div'}>
          <TruckImplementInfo dataProps={dataProps} isModal={isModal} truckId={truckId} props={props} />
        </Box>
        {!loadingDevices ? (
          <GridContainerInfoCards justifyContent={'center'} container>
            <Grid item>
              <ButtonIcon to={`${buttonRoute}/journey`}>
                <DivImg>
                  <RouteOutlined />
                </DivImg>
                <SpanText>Jornada</SpanText>
              </ButtonIcon>
            </Grid>
            <Grid item>
              <ButtonIcon to={`${buttonRoute}/telemetry`}>
                <DivImg>
                  <CellTowerOutlined width={'100px'} />
                </DivImg>
                <SpanText>Telemetria</SpanText>
              </ButtonIcon>
            </Grid>
            <Grid item style={showElement(showButtonByDevice([DeviceType.EBS]))}>
              <ButtonIcon to={`${buttonRoute}/load`}>
                <DivImg>
                  <ScaleOutlined />
                </DivImg>
                <SpanText>Carga</SpanText>
              </ButtonIcon>
            </Grid>
            <Grid item style={showElement(showButtonByDevice([DeviceType.TPMS]))}>
              <ButtonIcon to={`${buttonRoute}/tire-sensor-data`}>
                <DivImg>
                  <TireRepairOutlined width={'100px'} />
                </DivImg>
                <SpanText>TPMS</SpanText>
              </ButtonIcon>
            </Grid>
            <Grid item style={showElement(isAdmin || (isTransporterAdmin && isTireAuthorizedByCompany(companyId)))}>
              <ButtonIcon to={`${buttonRoute}/tire`}>
                <DivImg>
                  <TireRepairOutlined width={'100px'} />
                </DivImg>
                <SpanText>Pneus</SpanText>
              </ButtonIcon>
            </Grid>
            <Grid item style={showElement(!isChecklist)}>
              <ButtonIcon to={`${buttonRoute}/driver`}>
                <DivImg>
                  <BadgeOutlined />
                </DivImg>
                <SpanText>Motorista</SpanText>
              </ButtonIcon>
            </Grid>
            {/* <Grid item>
              <ButtonIcon to={`${buttonRoute}/report`}>
                <DivImg>
                  <InsertChartOutlined />
                </DivImg>
                <SpanText>Relatórios</SpanText>
              </ButtonIcon>
            </Grid> */}
            <Grid item style={showElement(blockScreen && showButtonByDevice([DeviceType.ST4305, DeviceType.ST4315, DeviceType.ST8310]))}>
              <ButtonIcon to={`${buttonRoute}/block`}>
                <DivImg>
                  <BlockOutlined />
                </DivImg>
                <SpanText>Bloqueio</SpanText>
              </ButtonIcon>
            </Grid>
            <Grid item style={showElement(!isChecklist)}>
              <ButtonIcon to={alertsRoute}>
                <DivImg>
                  <WarningAmberOutlined />
                </DivImg>
                <SpanText>Alertas</SpanText>
              </ButtonIcon>
            </Grid>
            {(isAdmin || isTransporterAdmin) && hasCamera && (
              <Grid item>
                <ButtonIcon to={camerasRoute}>
                  <DivImg>
                    <VideocamOutlined />
                  </DivImg>
                  <SpanText>Câmeras</SpanText>
                </ButtonIcon>
              </Grid>
            )}
            <Grid item style={showElement(isAdmin || isManufacturerAdmin || isRepresentativeAdmin || isTransporterAdmin)}>
              <ButtonIcon
                to={`/asset-classification/dashboard${truckImplement?.licensePlate ? `?licensePlate=${encodeURIComponent(truckImplement.licensePlate)}` : ''}`}
              >
                <DivImg>
                  <AssessmentOutlined />
                </DivImg>
                <SpanText>Classificação</SpanText>
              </ButtonIcon>
            </Grid>
            <Grid item style={showElement(isAdmin || isTransporterAdmin)}>
              <ButtonIcon to={`${buttonRoute}/inspection`}>
                <DivImg>
                  <ContentPasteSearchOutlined />
                </DivImg>
                <SpanText>Inspeção</SpanText>
              </ButtonIcon>
            </Grid>
            <Grid item style={showElement(isAdmin)}>
              <ButtonIcon to={`${buttonRoute}/devices-sensors`}>
                <DivImg>
                  <SensorsOutlined></SensorsOutlined>
                </DivImg>
                <SpanText>Equipamentos</SpanText>
              </ButtonIcon>
            </Grid>
            
            <Grid item style={showElement(isAdmin || isTransporterAdmin)}>
              <ButtonIcon to={`/maintenance-vehicle-custom/${truckId}/${maintenanceControlCustom ? maintenanceControlCustom.id : null}/new`}>
                <DivImg>
                  <BuildOutlined />
                </DivImg>
                <SpanText>
                  {maintenanceControlCustom && maintenanceControlCustom?.maintenance && (
                    <small className="text-warning ml-1 mt-n2 float-right">
                      <FontAwesomeIcon icon="circle" size="xs" />
                    </small>
                  )}
                  Manutenção
                </SpanText>
              </ButtonIcon>
            </Grid>
          </GridContainerInfoCards>
        ) : (
          <Loading />
        )}
      </BoxContainerTruckImplementInfo>
    </ThemeProvider>
  );
};

const DivImg = styled('div')(({ theme }) => ({
  borderRadius: '50%',
  width: '3rem',
  height: '3rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: theme.palette.mode === ColorScheme.DARK ? '' : '2px solid #D5D5D5',
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,
}));

const BoxContainerTruckImplementInfo = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  maxWidth: '750px',
  padding: '10px 10x 10px 10px',
  borderRadius: '16px',
  margiBottom: '1em',
  width: '100%',
  [theme.breakpoints.down('md')]: {
    padding: '3px',
  },
}));

const GridContainerInfoCards = styled(Grid)(({ theme }) => ({
  width: '100%',
  justifyContent: 'center',
  display: 'flex',
  padding: '1em 1em',
  margin: '0',
  borderRadius: '5px 5px 15px 15px',
  [theme.breakpoints.down('md')]: {
    padding: '1px 1px',
  },
}));

const SpanText = styled('span')(({ theme }) => ({
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: '400',
  textAlign: 'center',
  letterSpacing: '0.15px',
  textTransform: 'none',
  color: theme.palette.text.primary,
  [theme.breakpoints.down('xl')]: {
    fontSize: '14px',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '12px',
  },
}));
